import type {
  IFlag,
  IFlagEnvironment,
  IFlagEnvironmentInput,
  IFlagInput,
  IFlagsPayload,
  IWorkOSEnvironment,
  IWorkOSOrganisation,
} from '../types';

const KEY = 'nexus-ui.mock.flags';

export const ENVIRONMENTS: IWorkOSEnvironment[] = [
  { id: 'environment_01JZX6R49G8ACSWMDZKD0PQAV4', name: 'Hub', stage: 'prod', sandbox: false },
  {
    id: 'environment_01J6AEASRQRXSWN1VD6TK858NF',
    name: 'Staging',
    stage: 'staging',
    sandbox: true,
  },
  {
    id: 'environment_01KWQDTFVD9WCQ7YFCHWN8FHDM',
    name: 'Development',
    stage: 'dev',
    sandbox: true,
  },
  {
    id: 'environment_01J6AEAT5XSSTH7MBBJZBWJM5R',
    name: 'Developer Portal',
    stage: null,
    sandbox: false,
  },
];

const [HUB, STAGING, DEV, PORTAL] = ENVIRONMENTS.map(({ id }) => id);

export const ORGANISATIONS: IWorkOSOrganisation[] = [
  { id: 'org_01K072BNP83A74M0MVPPM69CG2', name: 'Azzurri', environment_id: HUB, users: 56 },
  {
    id: 'org_01KAZX05JJ27SXSS02DP6N5K01',
    name: 'hallandwoodhouse',
    environment_id: HUB,
    users: 16,
  },
  { id: 'org_01KR0TJ6Y23FTPPC229W2P2SWC', name: 'burgerking', environment_id: HUB, users: 9 },
  { id: 'org_01KPAKBXJX51NNNPPCJZV548Q0', name: 'bigtablegroup', environment_id: HUB, users: 4 },
  { id: 'org_01K1X6J407KP5QPT177QB3EEAK', name: 'Stonegate', environment_id: HUB, users: 1 },
  { id: 'org_01M2952MRM7Z8HGHCRY8SRT4CB', name: 'heartwood', environment_id: STAGING, users: 6 },
  {
    id: 'org_01K9CV8EB9NWF4DC4F9KZG5CGB',
    name: 'hallandwoodhouse',
    environment_id: STAGING,
    users: 14,
  },
  { id: 'org_01K9CB2G9WEJ1YKCAE3W0KME9N', name: 'openr', environment_id: STAGING, users: 6 },
  { id: 'org_01K88F14FD9KMDJNJD6MG6XKJK', name: 'demo', environment_id: STAGING, users: 13 },
  { id: 'org_01K832E0ZNP8RQG6SQT4CQ0P0P', name: 'burgerking', environment_id: STAGING, users: 13 },
  { id: 'org_01K6J5K5S86TF55PWXDHPV59QD', name: 'fullers', environment_id: STAGING, users: 4 },
  {
    id: 'org_01JZ7THA9988SRM0YYTQ5SQ5PA',
    name: 'bigtablegroup',
    environment_id: STAGING,
    users: 10,
  },
  { id: 'org_01J96XW8ESH67ZS0BC5SE62NPC', name: 'Stonegate', environment_id: STAGING, users: 11 },
  { id: 'org_01J6TCWC9YJK53PS03SAKP4V9Z', name: 'Azzurri', environment_id: STAGING, users: 33 },
  { id: 'org_dev_burgerking', name: 'burgerking', environment_id: DEV, users: 5 },
  { id: 'org_dev_azzurri', name: 'Azzurri', environment_id: DEV, users: 8 },
  { id: 'org_dev_openr', name: 'openr', environment_id: DEV, users: 6 },
];

const env = (
  id: string,
  enabled: boolean,
  defaultEnabled = enabled,
  access: IFlagEnvironment['access'] = enabled ? 'ALL' : 'NONE',
  orgs: string[] = [],
): IFlagEnvironment => ({
  id: `flag_environment_${id.slice(-8)}_${Math.random().toString(36).slice(2, 6)}`,
  environment_id: id,
  enabled,
  default_enabled: defaultEnabled,
  access,
  organisation_ids: orgs,
  user_ids: [],
});

const flag = (
  slug: string,
  name: string,
  description: string | null,
  owner: string | null,
  tags: string[],
  envs: IFlagEnvironment[],
  created: string,
): IFlag => ({
  id: `flag_${slug
    .replace(/[^a-z0-9]/gi, '')
    .toUpperCase()
    .slice(0, 16)}`,
  slug,
  name,
  description,
  owner_email: owner,
  tags,
  created_at: created,
  updated_at: created,
  environments: envs,
});

/** The sixteen flags that exist in the openr WorkOS project on 2026-10-22, with their real per-environment state. */
const seed = (): IFlag[] => [
  flag(
    'item_search_v2',
    'Item Search v2',
    'Multi-source product library search (AZ-8571).',
    'alex.satur@weareopenr.com',
    [],
    [env(HUB, false), env(STAGING, true), env(DEV, true), env(PORTAL, false)],
    '2026-10-19T15:01:03Z',
  ),
  flag(
    'menu_item_drag_and_drop',
    'Menu Item Drag & Drop',
    null,
    null,
    [],
    [env(HUB, true), env(STAGING, true), env(DEV, true), env(PORTAL, false)],
    '2026-09-22T21:07:33Z',
  ),
  flag(
    'menu-sets',
    'menu-sets',
    'Feature flag for Menu Sets functionality',
    'katia.toboleva@weareopenr.com',
    [],
    [env(HUB, false), env(STAGING, true), env(DEV, true), env(PORTAL, false)],
    '2026-09-07T14:19:39Z',
  ),
  flag(
    'venue-access-management',
    'Venue Access Management',
    null,
    null,
    ['venue-access'],
    [
      env(HUB, false),
      env(STAGING, true, false, 'SOME', ['org_01K832E0ZNP8RQG6SQT4CQ0P0P']),
      env(DEV, true),
      env(PORTAL, false),
    ],
    '2026-08-26T09:45:31Z',
  ),
  flag(
    'timetables',
    'timetables',
    'Feature flag for Timetables functionality: sidebar dropdown and page',
    'katia.toboleva@weareopenr.com',
    [],
    [env(HUB, false), env(STAGING, true), env(DEV, true), env(PORTAL, false)],
    '2026-08-05T15:03:11Z',
  ),
  flag(
    'new_dashboard',
    'new dashboard',
    'Feature flag for new dashboard layout',
    'ineta.bliudziute@weareopenr.com',
    [],
    [env(HUB, true), env(STAGING, true), env(DEV, true), env(PORTAL, false)],
    '2026-07-06T15:11:11Z',
  ),
  flag(
    'enhanced_scheduling',
    'enhanced_scheduling',
    'Feature flag for enhanced schedule setup',
    'ineta.bliudziute@weareopenr.com',
    [],
    [
      env(HUB, true),
      env(STAGING, true, false, 'SOME', ['org_01K832E0ZNP8RQG6SQT4CQ0P0P']),
      env(DEV, true, false, 'SOME', ['org_dev_burgerking']),
      env(PORTAL, false),
    ],
    '2026-05-12T12:44:36Z',
  ),
  flag(
    'independent_option_groups',
    'independent_option_groups',
    'Independent option group functionality. Available for non-Assure65 tenants (everyone but Azzurri). Any new tenant should be added here.',
    'katia.toboleva@weareopenr.com',
    [],
    [
      env(HUB, true, false, 'SOME', [
        'org_01KR0TJ6Y23FTPPC229W2P2SWC',
        'org_01KAZX05JJ27SXSS02DP6N5K01',
        'org_01KPAKBXJX51NNNPPCJZV548Q0',
        'org_01K1X6J407KP5QPT177QB3EEAK',
      ]),
      env(STAGING, true, false, 'SOME', [
        'org_01K832E0ZNP8RQG6SQT4CQ0P0P',
        'org_01K9CV8EB9NWF4DC4F9KZG5CGB',
      ]),
      env(DEV, true, false, 'SOME', ['org_dev_burgerking']),
      env(PORTAL, false),
    ],
    '2026-04-27T13:33:17Z',
  ),
  flag(
    'export-menu-to-design-platform',
    'Export menu to Design Platform',
    'Allow users to see export to design platform',
    'alex.satur@weareopenr.com',
    [],
    [env(HUB, false), env(STAGING, true), env(DEV, true), env(PORTAL, false)],
    '2026-03-26T09:25:49Z',
  ),
  flag(
    'pricing-rules',
    'Pricing Rules',
    'Show pricing rules in a new tab or in 2 separate legacy tabs',
    'kai.roper-blackman@weareopenr.com',
    [],
    [env(HUB, true), env(STAGING, true), env(DEV, true), env(PORTAL, false)],
    '2026-03-24T13:23:16Z',
  ),
  flag(
    'pricing_categories',
    'pricing_categories',
    'Show/hide Pricing Categories tab in Pricing Schemes',
    'katia.toboleva@weareopenr.com',
    [],
    [env(HUB, true), env(STAGING, true), env(DEV, true), env(PORTAL, true)],
    '2026-03-10T16:20:45Z',
  ),
  flag(
    'venue_categories',
    'venue_categories',
    'Show/hide Venue Categories tab on Venues page',
    'katia.toboleva@weareopenr.com',
    [],
    [env(HUB, true), env(STAGING, true), env(DEV, true), env(PORTAL, false)],
    '2026-01-21T10:08:31Z',
  ),
  flag(
    'product_publish_flow',
    'Product Publishing Flow',
    'Show/ hide the new product publishing flow in the product library',
    'kai.roper-blackman@weareopenr.com',
    [],
    [env(HUB, true), env(STAGING, true), env(DEV, true), env(PORTAL, false)],
    '2025-11-26T14:39:24Z',
  ),
  flag(
    'deposit_return_scheme',
    'deposit_return_scheme',
    'Show or hide the deposit return scheme',
    'kai.roper-blackman@weareopenr.com',
    [],
    [env(HUB, true), env(STAGING, true), env(DEV, true), env(PORTAL, false)],
    '2025-11-19T15:38:12Z',
  ),
  flag(
    'ent_menu-management',
    'Menu Management',
    null,
    null,
    ['entitlement'],
    [env(HUB, false), env(STAGING, true), env(DEV, true), env(PORTAL, false)],
    '2025-11-11T16:35:44Z',
  ),
  flag(
    'price-rules',
    'price rules',
    'Allow users to manage and apply price rules in menu manager via the price rules page.',
    'kai.roper-blackman@weareopenr.com',
    [],
    [env(HUB, true), env(STAGING, true), env(DEV, true), env(PORTAL, false)],
    '2025-07-29T13:39:43Z',
  ),
];

const read = (): IFlag[] => {
  try {
    const raw = globalThis.localStorage?.getItem(KEY);
    if (raw) {
      return JSON.parse(raw) as IFlag[];
    }
  } catch {
    // fall through
  }
  const seeded = seed();
  write(seeded);
  return seeded;
};

const write = (flags: IFlag[]) => {
  try {
    globalThis.localStorage?.setItem(KEY, JSON.stringify(flags));
  } catch {
    // storage unavailable
  }
};

export const mockFlags = (): IFlagsPayload => ({
  environments: ENVIRONMENTS,
  organisations: ORGANISATIONS,
  flags: read(),
  write_adapter: 'mock',
});

export const mockCreateFlag = (input: IFlagInput): IFlag => {
  const flags = read();
  if (flags.some((item) => item.slug === input.slug)) {
    throw { message: `A flag with slug ${input.slug} already exists`, status: 409 };
  }
  const now = new Date().toISOString();
  const record = flag(
    input.slug,
    input.name,
    input.description,
    null,
    input.tags,
    ENVIRONMENTS.map(({ id }) => env(id, false)),
    now,
  );
  write([...flags, record]);
  return record;
};

export const mockUpdateFlag = (id: string, input: IFlagInput): IFlag => {
  const flags = read();
  const index = flags.findIndex((item) => item.id === id);
  if (index === -1) throw { message: 'Flag not found', status: 404 };
  flags[index] = {
    ...flags[index],
    name: input.name,
    description: input.description,
    tags: input.tags,
    updated_at: new Date().toISOString(),
  };
  write(flags);
  return flags[index];
};

export const mockDeleteFlag = (id: string) => write(read().filter((item) => item.id !== id));

export const mockUpdateFlagEnvironment = (
  flagId: string,
  flagEnvironmentId: string,
  input: IFlagEnvironmentInput,
): IFlag => {
  const flags = read();
  const index = flags.findIndex((item) => item.id === flagId);
  if (index === -1) throw { message: 'Flag not found', status: 404 };
  flags[index] = {
    ...flags[index],
    updated_at: new Date().toISOString(),
    environments: flags[index].environments.map((item) =>
      item.id === flagEnvironmentId
        ? {
            ...item,
            enabled: input.enabled,
            default_enabled: input.default_enabled,
            access: input.access,
            organisation_ids: input.organisation_ids,
            user_ids: input.user_ids,
          }
        : item,
    ),
  };
  write(flags);
  return flags[index];
};
